import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, Signal, ViewChild } from '@angular/core';
import { DwGridDataSourceService, FieldTypes } from '@digiwall/ag-grid';
import {
  CustomerOpenOrderLinesService,
  CustomerOpenOrderLinesViewDto,
  DayBookService,
  DwGridRequestDto,
  FilterModel,
} from '@digiwall/goliath-documents/proxy';
import { LocalizationService, SubscriptionService, LocalizationModule } from '@abp/ng.core';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { ColDef } from '@ag-grid-community/core';
import { ListPageComponent } from '@digiwall/ag-grid';
import { AgGridComponent } from '@digiwall/ag-grid';
import { GoliathDocumentEntityNames } from '../../constants/entity-names.constants';
import { OpenOrderLinesListComponent } from '../open-order-lines/open-order-lines-list.component';
import { DropdownAction } from '@digiwall/ui-kit';
import { of, switchMap, throwError } from 'rxjs';
import { OdysseyOffcanvas } from '@digiwall/core';
import { SupplierOrderCreationDialogComponent } from './supplier-order-creation-dialog/supplier-order-creation-dialog.component';
import { DocRole } from '@digiwall/goliath-core/proxy';

@Component({
  selector: 'document-customer-open-order-lines-list',
  templateUrl: './customer-open-order-lines-list.component.html',
  styles: `
    :host {
      flex-grow: 1;
    }
  `,
  providers: [DwGridDataSourceService, SubscriptionService],
  imports: [CommonModule, ListPageComponent, AgGridComponent, LocalizationModule],
})
export class CustomerOpenOrderLinesListComponent extends OpenOrderLinesListComponent<
  CustomerOpenOrderLinesViewDto,
  CustomerOpenOrderLinesService
> {
  private daybookService = inject(DayBookService);
  private offCanvasService = inject(OdysseyOffcanvas);

  @ViewChild('agGrid') grid!: AgGridComponent;

  protected thirdPartyCode = input<string>();
  protected articleCode = input<string>();

  protected GoliathDocumentEntityNames: any = GoliathDocumentEntityNames;

  public columns = computed(() => {
    this.grid?.refreshGrid();
    if (this.thirdPartyCode()?.length > 0) {
      // Remove third party columns
      const toRemove = new Set<string>(['customerCode', 'customerName']);
      return this._columns.filter(x => !toRemove.has(x.field));
    }
    if (this.articleCode()?.length > 0) {
      // Remove article column
      const toRemove = new Set<string>(['articleCode']);
      return this._columns.filter(x => !toRemove.has(x.field));
    } else {
      return this._columns;
    }
  });

  private _columns: ColDef[] = [
    ...this.colDefs,
    {
      field: 'requestedDeliveryDate',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:RequestedDeliveryDate'),
      type: FieldTypes.Date,
    },
    {
      field: 'reservationState',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:ReservationState'),
      type: FieldTypes.String,
    },
    {
      field: 'deliveryState',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:DeliveryState'),
      type: FieldTypes.String,
    },
    {
      field: 'customerCode',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:CustomerCode'),
      type: FieldTypes.String,
    },
    {
      field: 'customerName',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:CustomerName'),
      type: FieldTypes.String,
    },
    {
      field: 'supplierCode',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:SupplierCode'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'supplierName',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:SupplierName'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'deliveredQuantity',
      headerName: this.localizationService.instant('Doc::CustomerSalesPurchase:DeliveredQuantity'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: 2,
      },
    },
    {
      field: 'reservedQuantity',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:ReservedQuantity'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: 2,
      },
    },
    {
      field: 'quantityOnSupplierOrder',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:QuantityOnSupplierOrder'),
      type: FieldTypes.Number,
      filterParams: {
        decimals: 2,
      },
      hide: true,
    },
    {
      field: 'articleRange1',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRange1'),
      type: FieldTypes.String,
    },
    {
      field: 'articleRangeValue1',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRangeValue1'),
      type: FieldTypes.String,
    },
    {
      field: 'articleRange2',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRange2'),
      type: FieldTypes.String,
    },
    {
      field: 'articleRangeValue2',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRangeValue2'),
      type: FieldTypes.String,
    },
    {
      field: 'articleLot',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleLot'),
      type: FieldTypes.String,
    },
    {
      field: 'articleSerial',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleSerial'),
      type: FieldTypes.String,
    },
    {
      field: 'productStrength',
      headerName: this.localizationService.instant('Product::Article:ProductStrength'),
      type: FieldTypes.String,
      hide: true,
    },
  ];

  protected toolbarActions: Signal<DropdownAction[]> = computed(() => {
    return [
      {
        label: 'Doc::GenerateSupplierOrders',
        icon: 'digi-out-synchronization',
        action: () => {
          this.generateSupplierOrders();
        },
      },
    ];
  });

  constructor(
    service: CustomerOpenOrderLinesService,
    datasource: DwGridDataSourceService,
    localizationService: LocalizationService,
    private confirmationService: ConfirmationService,
  ) {
    super(service, datasource, localizationService);
  }

  private generateSupplierOrders() {
    this.daybookService
      .getDayBooksByRole(DocRole.SupplierPurchaseOrder, true)
      .pipe(
        switchMap(daybooks => {
          if (daybooks.length === 0) {
            const error = new Error('Doc::SupplierOrderGeneration:NoDaybookConfigured');
            (error as any).type = 'Error';
            return throwError(() => error);
          }
          return of(daybooks);
        }),
        switchMap(daybooks => {
          if (this.selectedEntities().length === 0) {
            const error = new Error('Doc::SupplierOrderGeneration:NoOpenOrderLinesSelected');
            (error as any).type = 'Warning';
            return throwError(() => error);
          }
          return of(daybooks);
        }),
      )
      .subscribeOnce(
        daybooks => {
          const canvasRef = this.offCanvasService.open(SupplierOrderCreationDialogComponent, null, {
            position: 'end',
            panelClass: 'w-100 w-sm-75 w-3xl-50 ',
            backdrop: 'static',
          });

          const componentInstance =
            canvasRef.componentInstance as SupplierOrderCreationDialogComponent;
          componentInstance.daybooks.set(daybooks);
          componentInstance.selectedLineIds.set(this.selectedEntities().map(x => x.id));

          canvasRef.closed.subscribeOnce((result: any) => {
            if (result === true) {
              this.grid.refreshGrid();
            }
          });
        },
        (err: Error) => {
          const errorFn = (message, title, options) =>
            (err as any).type === 'Warning'
              ? this.confirmationService.warn(message, title, options)
              : this.confirmationService.error(message, title, options);

          errorFn(err.message, 'Doc::SupplierOrderGeneration', {
            hideCancelBtn: true,
            yesText: this.localizationService.instant('::Ok'),
          }).subscribeOnce();
        },
      );
  }

  protected override getQueryObservable() {
    const baseObservable = super.getQueryObservable();
    return (query: DwGridRequestDto) => {
      if (query?.request != null && this.thirdPartyCode()?.length > 0) {
        query.request.filterModel ??= {};
        query.request.filterModel.customerCode = {
          filter: this.thirdPartyCode() as unknown as object,
          filterType: 'text',
          type: 'equals',
        } as FilterModel;
      }
      if (query?.request != null && this.articleCode()?.length > 0) {
        query.request.filterModel ??= {};
        query.request.filterModel.articleCode = {
          filter: this.articleCode() as unknown as object,
          filterType: 'text',
          type: 'equals',
        } as FilterModel;
      }
      return baseObservable(query);
    };
  }

  public override selectedRowsClicked($event: Array<CustomerOpenOrderLinesViewDto>) {
    $event.forEach(entity => {
      const url = '/documents/sales-purchases/' + entity.docId;
      window.open(url, '_blank');
    });
  }
}
